import React from 'react';
import { Trans } from 'react-i18next';
import {
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { Close, EditNote, RssFeed } from '@mui/icons-material';

import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Typography } from 'src/components/Typography';
import { BUTTON_COLOR, BUTTON_SIZE, BUTTON_VARIANT } from 'src/components/Button/types';
import { TYPOGRAPHY_WEIGHT } from 'src/components/Typography/types';
import { ParseJobsFromFeed } from '../ParseJobsFromFeed/ParseJobsFromFeed';
import { CreateJobDialog } from 'src/pages/Job/components/CreateJobDialog';

import { JobCreationDialogProps } from './JobCreationDialog.types';

export const JobCreationDialog: React.FC<JobCreationDialogProps> = ({ jobCreation }) => {
  const {
    isJobTypeDialogOpen,
    isManualJobDialogOpen,
    parsedJobData,
    parsedRoleData,
    dialogKey,
    feedImport,
    closeJobTypeDialog,
    openManualJobDialog,
    openFeedDialog,
    setManualJobDialogOpen,
    handleFeedJobSaved,
  } = jobCreation;

  const isImporting = feedImport.isImporting;

  const parseJobsFromFeedLabel =
    feedImport.parsedJobs.length > 0 ? (
      <Trans>jobPage.useImportedJobs</Trans>
    ) : (
      <Trans>jobPage.importFromFeed</Trans>
    );

  return (
    <Container>
      <Dialog open={isJobTypeDialogOpen} onClose={closeJobTypeDialog} maxWidth="xs">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={TYPOGRAPHY_WEIGHT.MEDIUM}>
              <Trans>jobPage.selectJobType</Trans>
            </Typography>
            <Close onClick={closeJobTypeDialog} sx={{ cursor: 'pointer' }} />
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2, pb: 3 }}>
          <Stack spacing={2} mt={1}>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              size={BUTTON_SIZE.MEDIUM}
              color={BUTTON_COLOR.INFO}
              onClick={openManualJobDialog}>
              <Box display="flex" alignItems="center">
                <EditNote sx={{ mr: 1 }} />
                <Trans>jobPage.manualJobCreation</Trans>
              </Box>
            </Button>
            <Button
              variant={BUTTON_VARIANT.GRADIENT}
              size={BUTTON_SIZE.MEDIUM}
              color={BUTTON_COLOR.DARK}
              disabled={isImporting}
              onClick={openFeedDialog}>
              <Box display="flex" alignItems="center">
                {isImporting && <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />}
                <RssFeed sx={{ mr: 1 }} />
                {parseJobsFromFeedLabel}
              </Box>
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      <CreateJobDialog
        key={dialogKey}
        open={isManualJobDialogOpen}
        setOpen={setManualJobDialogOpen}
        editJobData={parsedJobData}
        editRoleData={parsedRoleData}
        onSuccessCallback={feedImport.isImporting ? handleFeedJobSaved : undefined}
        onCancel={() => {
          feedImport.stopImporting();
        }}
      />
      <ParseJobsFromFeed feedImport={feedImport} />
    </Container>
  );
};
